import { useEffect, useState } from "react";
import type { HouseholdDetail } from "@guestbook/shared";
import { Button } from "@/shared/components/ui/Button";
import { Checkbox } from "@/shared/components/ui/Checkbox";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/Dialog";
import { Input } from "@/shared/components/ui/Input";
import { useContactsList } from "@/shared/lib/contacts";
import { useUpdateHousehold } from "@/shared/lib/households";
import { cn } from "@/shared/lib/utils";

const PAGE_LIMIT = 50;

function householdName(hh: HouseholdDetail): string {
  return hh.displayNameCustom ?? "Unnamed household";
}

interface EditHouseholdMembersDialogProps {
  household: HouseholdDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Membership editor shown from the household detail "Edit members" button.
 * Lists existing contacts with the current members pre-selected; toggling a
 * row stages an add or a remove. Saving sends the net diff in one PATCH so a
 * single round trip both attaches and detaches contacts. Search is delegated
 * to the contacts list endpoint.
 */
export function EditHouseholdMembersDialog({
  household,
  open,
  onOpenChange,
}: EditHouseholdMembersDialogProps) {
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set(household.members.map((m) => m.id)),
  );

  const update = useUpdateHousehold(household.id);
  const candidates = useContactsList({
    search: search || undefined,
    limit: PAGE_LIMIT,
  });
  const items = candidates.data?.items ?? [];

  const memberIds = new Set(household.members.map((m) => m.id));
  // Stable primitive so the seed effect doesn't re-run on unrelated re-renders
  // (e.g. a background refetch returning the same roster) and clobber an edit.
  const memberKey = [...memberIds].sort().join(",");

  // Re-seed the selection each time the dialog opens so it mirrors the current
  // roster and discards any abandoned prior edit.
  useEffect(() => {
    if (open) {
      setSelectedIds(new Set(memberKey ? memberKey.split(",") : []));
      setSearch("");
    }
  }, [open, memberKey]);

  const add = [...selectedIds].filter((id) => !memberIds.has(id));
  const remove = [...memberIds].filter((id) => !selectedIds.has(id));
  const hasChanges = add.length > 0 || remove.length > 0;

  function toggleRow(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function handleSubmit() {
    update.mutate(
      { members: { add, remove } },
      {
        onSuccess: () => onOpenChange(false),
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="2xl" className="max-h-[85vh] gap-5">
        <DialogHeader>
          <DialogTitle>Edit members of {householdName(household)}</DialogTitle>
          <DialogDescription>
            Select the contacts who belong to this household.
          </DialogDescription>
        </DialogHeader>

        <Input
          type="search"
          placeholder="Search by name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search contacts"
        />

        <div className="max-h-[50vh] overflow-y-auto rounded-xl border border-border-muted glass-surface-subtle">
          {candidates.isLoading ? (
            <p className="p-4 type-body-base text-text-muted">Loading…</p>
          ) : candidates.isError ? (
            <p className="p-4 type-body-base text-danger-strong">
              Couldn't load contacts: {candidates.error.message}
            </p>
          ) : items.length === 0 ? (
            <p className="p-6 text-center type-body-base text-text-muted">
              {search ? "No matching contacts." : "No contacts yet."}
            </p>
          ) : (
            <ul className="flex flex-col">
              {items.map((c) => {
                const selected = selectedIds.has(c.id);
                const name = c.name || "—";
                const secondary = c.email ?? c.phone ?? "—";
                return (
                  <li key={c.id}>
                    <label
                      className={cn(
                        "flex cursor-pointer items-center gap-3 border-t border-border-muted px-4 py-2.5 first:border-t-0",
                        "transition-colors duration-150 hover:bg-surface-muted/40",
                        selected && "bg-surface-muted/30",
                      )}
                    >
                      <Checkbox
                        checked={selected}
                        onCheckedChange={() => toggleRow(c.id)}
                        aria-label={`Select ${name}`}
                      />
                      <span className="flex min-w-0 flex-col">
                        <span className="truncate type-body-base text-text">
                          {name}
                        </span>
                        <span className="truncate type-body-sm text-text-muted">
                          {secondary}
                        </span>
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {(candidates.data?.total ?? 0) > items.length ? (
          <p className="type-body-sm text-text-muted">
            More results — refine your search to narrow them down.
          </p>
        ) : null}

        {update.error ? (
          <p className="type-body-sm text-danger-strong">
            Couldn't save members: {update.error.message}
          </p>
        ) : null}

        <div className="flex flex-row-reverse gap-2">
          <Button onClick={handleSubmit} disabled={!hasChanges || update.isPending}>
            {update.isPending ? "Saving…" : "Save"}
          </Button>
          <DialogClose asChild>
            <Button variant="ghost">Cancel</Button>
          </DialogClose>
        </div>
      </DialogContent>
    </Dialog>
  );
}
